import { db } from '../../firebase';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { pwaLiveWriteRegionError } from './ordersSurface';

const PWA_JOBS_PATH = '/app/orders';

export const CREATE_PWA_JOB_WRITE = 'firestore.jobs.add';

export const CREATE_PWA_JOB_CONFIRM_TITLE = 'Create pending job? (live write)';
export const CREATE_PWA_JOB_CONFIRM =
    'LIVE WRITE. Create a pending job assigned to you? Firestore add writes status pending on this tech. Cancel shows CREATE · CANCELLED and leaves Firestore unchanged. This is not CREATE · SOT — jobsSot=empty never writes. Zapier is not part of this path. No new callable.';

export const TRUE_EMPTY_WALK_CONFIRM_TITLE = 'Confirm → Firestore add? (walk only)';
export const TRUE_EMPTY_WALK_CONFIRM =
    'WALK ONLY — Firestore add will not run. This is the same confirm step as the live true-empty path. On a real zero-job tech, confirming writes pending assigned to you. Assigned jobs stay unchanged. Cancel paints CREATE · CANCELLED and does not call Firestore add. This is not CREATE · SOT.';

export const CREATE_SOT = {
    eyebrow: 'CREATE · SOT',
    headline: 'Paint-only — Firestore add did not run',
    body:
        'jobsSot=empty is not a true-empty tech. There was no confirm dialog. Firestore add did not run. Live assigned jobs are unchanged. The live true-empty path is confirm → Firestore add pending assigned to you.',
    cta: 'Exit SoT',
    href: PWA_JOBS_PATH,
    note:
        'This is not a live write. Hosting honors jobsSot for a signed-in tech. Use TRUE EMPTY · WALK (?trueEmptySot=walk) to see the confirm step without emptying a playground tech. No new callable.',
};

export const CREATE_TRUE_EMPTY_CONFIRM = {
    eyebrow: 'TRUE EMPTY · CONFIRM',
    headline: 'Confirm step complete — no write',
    body:
        'Firestore add did not run. Live assigned jobs are unchanged. On a real zero-job tech, this same confirm writes pending assigned to you. SoT EMPTY (jobsSot=empty) never shows this confirm.',
    cta: 'Exit walk',
    href: PWA_JOBS_PATH,
    note:
        'Hosting honors trueEmptySot for a signed-in tech. This walk is not CREATE · SOT and not a live write.',
};

export const CREATE_PREVIEW = {
    eyebrow: 'CREATE · PREVIEW',
    headline: 'Local preview — Firestore add did not run',
    body:
        'JOBS · EMPTY is a local CRA preview. Hosting production ignores jobsPreview. Nothing was written to this tech.',
    cta: 'Back',
    note: 'Use ?jobsSot=empty on Hosting to prove the same Create job CTA without a zero-job tech.',
};

export const CREATE_OFFLINE = {
    eyebrow: 'CREATE · OFFLINE',
    headline: 'You are offline',
    body:
        'The tech PWA cannot create a job without a connection. This is not an empty assigned list and not a successful create. Firestore add was not called.',
    cta: 'Retry',
    note: 'Reconnect, then create. Cached counts stay hidden. This is not JOBS · EMPTY.',
};

export const CREATE_DENIED = {
    eyebrow: 'CREATE · DENIED',
    headline: 'Tech session is incomplete',
    body:
        'Create job needs a signed-in tech with techId and a region collection. The PWA will not invent a tech or write a job without those.',
    cta: 'Back',
    note: 'Sign in as a Glass tech. This is not JOBS · EMPTY.',
};

export const CREATE_FIXTURE_REGION = {
    eyebrow: 'CREATE · DENIED',
    headline: 'Fixture region is not a live write',
    body: 'preview-test-region is a Hosting fixture label, not a Firestore region. Firestore add did not run.',
    cta: 'Back',
    note:
        'Set tech claims regionId to the Firestore region named Test Region, then sign in again. jobsSot walks may still label Test Region as preview-test-region. That label does not write.',
};

export const CREATE_ERROR = {
    eyebrow: 'CREATE · ERROR',
    headline: 'Assigned job could not be written',
    body:
        'The pending job was not written. This is not an empty assigned list and not a successful create. Status stays unchanged. The tech PWA does not invent success after a failed Firestore add.',
    cta: 'Retry',
    note:
        'Confirm you are a Glass tech on techrepair-glass, then retry. Live create is Firestore add — no new callable. Zapier is not part of this path.',
};

export const CREATE_PENDING = {
    eyebrow: 'CREATE · PENDING',
    headline: 'Job is pending',
    body:
        'Firestore add wrote status pending assigned to you. Orders does not invent a second success card after the list refresh.',
    cta: 'Back to jobs',
    note: 'The new job appears on the live assigned list after this modal closes. This is not JOBS · EMPTY.',
};

/** Confirm dismiss is a visible create card. Firestore add did not run. This is not a callable failure. */
export const CREATE_CANCELLED = {
    eyebrow: 'CREATE · CANCELLED',
    headline: 'Create confirm was cancelled',
    body:
        'The confirm dialog was cancelled or dismissed. Firestore add was not called. No pending job was written. The form can be retried. This is not a callable failure.',
    cta: 'Back to form',
    note:
        'Accept the confirm to continue. Cancel writes nothing. Back to form keeps the create form usable. Live create is Firestore add — no new callable. This is not CREATE · ERROR.',
};

export const CREATE_FIXTURE_NOTICE: Record<string, string> = {
    sot:
        'CREATE · SOT — submit skips confirm and does not call Firestore add. Live assigned jobs are unchanged. This is not the true-empty live write.',
    preview:
        'CREATE · PREVIEW — local CRA only. Submit skips confirm and does not call Firestore add. Hosting production ignores jobsPreview.',
    'true-empty':
        'TRUE EMPTY · WALK — submit shows the confirm dialog. Firestore add will not run. Live assigned jobs are unchanged. On a real zero-job tech the same confirm writes pending assigned to you.',
    'true-empty-preview':
        'TRUE EMPTY · PREVIEW — local CRA only. Submit shows the confirm dialog. Firestore add will not run. Hosting production ignores trueEmptyPreview.',
};

export const CREATE_LIVE_EMPTY_NOTICE =
    'TRUE EMPTY · LIVE — submit asks to confirm, then Firestore add writes pending assigned to you. This is not CREATE · SOT (jobsSot=empty never writes).';

export const PWA_CREATE_SOT_VALUES = ['error', 'offline', 'denied'] as const;
export const PWA_CREATE_PREVIEW_VALUES = PWA_CREATE_SOT_VALUES;
export const PWA_CREATE_PAINT_FIXTURES = ['sot', 'preview', ...PWA_CREATE_SOT_VALUES] as const;
export const PWA_TRUE_EMPTY_SOT_VALUES = ['walk'] as const;
export const PWA_TRUE_EMPTY_PREVIEW_VALUES = PWA_TRUE_EMPTY_SOT_VALUES;
export const PWA_TRUE_EMPTY_WALK_FIXTURES = ['true-empty', 'true-empty-preview'] as const;

export type PwaCreateSotSurface = typeof PWA_CREATE_SOT_VALUES[number];
export type PwaTrueEmptySotSurface = typeof PWA_TRUE_EMPTY_SOT_VALUES[number];
export type PwaCreateFixture =
    | typeof PWA_CREATE_PAINT_FIXTURES[number]
    | typeof PWA_TRUE_EMPTY_WALK_FIXTURES[number]
    | null;

export const PWA_CREATE_SOT_NOTICE =
    'PWA SoT fixture — CREATE · ERROR / OFFLINE / DENIED helper cards. Hosting honors createSot for a signed-in tech. Firestore add did not run. Live assigned jobs are unchanged without this query.';

export const PWA_CREATE_PREVIEW_NOTICE =
    'Local preview — CREATE · ERROR / OFFLINE / DENIED helper cards. Hosting production ignores createPreview.';

export const PWA_CREATE_SOT_DETAIL =
    'PWA SoT fixture — Firestore add did not run. Live assigned jobs are unchanged.';

export const PWA_CREATE_PREVIEW_DETAIL =
    'Local preview — Firestore add did not run. Hosting production ignores createPreview.';

export const PWA_TRUE_EMPTY_SOT_NOTICE =
    'TRUE EMPTY · WALK — confirm → Firestore add path without emptying a playground tech. Hosting honors trueEmptySot for a signed-in tech. Firestore add did not run. Live assigned jobs are unchanged. On a real zero-job tech the same confirm writes pending assigned to you.';

export const PWA_TRUE_EMPTY_PREVIEW_NOTICE =
    'TRUE EMPTY · PREVIEW — local CRA only. Confirm step without a write. Hosting production ignores trueEmptyPreview.';

export const PWA_TRUE_EMPTY_SOT_DETAIL =
    'TRUE EMPTY · WALK — Firestore add did not run. Live assigned jobs are unchanged.';

export const PWA_TRUE_EMPTY_PREVIEW_DETAIL =
    'TRUE EMPTY · PREVIEW — Firestore add did not run. Hosting production ignores trueEmptyPreview.';

function queryParam(search: string | undefined, key: string): string | null {
    if (!search) return null;
    const raw = String(search).startsWith('?') ? String(search).slice(1) : String(search);
    return new URLSearchParams(raw).get(key);
}

export function pwaCreateSotSurface(search?: string): PwaCreateSotSurface | null {
    const value = queryParam(search, 'createSot');
    return (PWA_CREATE_SOT_VALUES as readonly string[]).includes(value || '')
        ? (value as PwaCreateSotSurface)
        : null;
}

/** Hosting honors createSot only for a signed-in tech. Local CRA can still use createPreview. */
export function pwaCreateSotAllowed(input: { techId?: string | null } = {}): boolean {
    return Boolean(input.techId);
}

export function pwaCreateSotActive(
    search?: string,
    input: { techId?: string | null } = {},
): PwaCreateSotSurface | null {
    if (!pwaCreateSotAllowed(input)) return null;
    const jobsSot = queryParam(search, 'jobsSot');
    if (
        jobsSot === 'empty' ||
        jobsSot === 'error' ||
        jobsSot === 'offline' ||
        jobsSot === 'pending' ||
        jobsSot === 'pending-handoff' ||
        jobsSot === 'handoff' ||
        jobsSot === 'complete-handoff'
    ) {
        return null;
    }
    return pwaCreateSotSurface(search);
}

export function pwaCreateSotHref(surface?: string): string {
    if (!surface || !(PWA_CREATE_SOT_VALUES as readonly string[]).includes(surface)) return PWA_JOBS_PATH;
    return `${PWA_JOBS_PATH}?createSot=${surface}`;
}

export function pwaCreatePreviewSurface(
    search?: string,
    env: string | undefined = process.env.NODE_ENV,
): PwaCreateSotSurface | null {
    if (env === 'production') return null;
    const value = queryParam(search, 'createPreview');
    return (PWA_CREATE_PREVIEW_VALUES as readonly string[]).includes(value || '')
        ? (value as PwaCreateSotSurface)
        : null;
}

/** Prefer Hosting-safe createSot, then CRA-only createPreview. */
export function pwaCreateFixtureSurface(
    search?: string,
    input: { techId?: string | null } = {},
    env: string | undefined = process.env.NODE_ENV,
): PwaCreateSotSurface | null {
    return pwaCreateSotActive(search, input) || pwaCreatePreviewSurface(search, env);
}

export function pwaCreateFixtureNotice(
    search?: string,
    input: { techId?: string | null } = {},
    env: string | undefined = process.env.NODE_ENV,
): string | null {
    if (pwaCreateSotActive(search, input)) return PWA_CREATE_SOT_NOTICE;
    if (pwaCreatePreviewSurface(search, env)) return PWA_CREATE_PREVIEW_NOTICE;
    return null;
}

export function pwaCreateFixtureDetail(kind?: string | null): string {
    if (kind === 'sot') return PWA_CREATE_SOT_DETAIL;
    if (kind === 'true-empty') return PWA_TRUE_EMPTY_SOT_DETAIL;
    if (kind === 'true-empty-preview') return PWA_TRUE_EMPTY_PREVIEW_DETAIL;
    return PWA_CREATE_PREVIEW_DETAIL;
}

export function pwaTrueEmptySotSurface(search?: string): PwaTrueEmptySotSurface | null {
    const value = queryParam(search, 'trueEmptySot');
    return (PWA_TRUE_EMPTY_SOT_VALUES as readonly string[]).includes(value || '')
        ? (value as PwaTrueEmptySotSurface)
        : null;
}

/** Hosting honors trueEmptySot only for a signed-in tech. Local CRA can still use trueEmptyPreview. */
export function pwaTrueEmptySotAllowed(input: { techId?: string | null } = {}): boolean {
    return Boolean(input.techId);
}

export function pwaTrueEmptySotActive(
    search?: string,
    input: { techId?: string | null } = {},
): PwaTrueEmptySotSurface | null {
    if (!pwaTrueEmptySotAllowed(input)) return null;
    return pwaTrueEmptySotSurface(search);
}

export function pwaTrueEmptySotHref(surface = 'walk'): string {
    if (!(PWA_TRUE_EMPTY_SOT_VALUES as readonly string[]).includes(surface)) return PWA_JOBS_PATH;
    return `${PWA_JOBS_PATH}?trueEmptySot=${surface}`;
}

export function pwaTrueEmptyPreviewSurface(
    search?: string,
    env: string | undefined = process.env.NODE_ENV,
): PwaTrueEmptySotSurface | null {
    if (env === 'production') return null;
    const value = queryParam(search, 'trueEmptyPreview');
    return (PWA_TRUE_EMPTY_PREVIEW_VALUES as readonly string[]).includes(value || '')
        ? (value as PwaTrueEmptySotSurface)
        : null;
}

/** Prefer Hosting-safe trueEmptySot, then CRA-only trueEmptyPreview. */
export function pwaTrueEmptyFixtureSurface(
    search?: string,
    input: { techId?: string | null } = {},
    env: string | undefined = process.env.NODE_ENV,
): PwaTrueEmptySotSurface | null {
    return pwaTrueEmptySotActive(search, input) || pwaTrueEmptyPreviewSurface(search, env);
}

export function pwaTrueEmptyFixtureNotice(
    search?: string,
    input: { techId?: string | null } = {},
    env: string | undefined = process.env.NODE_ENV,
): string | null {
    if (pwaTrueEmptySotActive(search, input)) return PWA_TRUE_EMPTY_SOT_NOTICE;
    if (pwaTrueEmptyPreviewSurface(search, env)) return PWA_TRUE_EMPTY_PREVIEW_NOTICE;
    return null;
}

export function pwaTrueEmptyWalkKind(
    sotActive?: string | null,
    previewActive?: string | null,
): 'true-empty' | 'true-empty-preview' | null {
    if (sotActive) return 'true-empty';
    if (previewActive) return 'true-empty-preview';
    return null;
}

export function isTrueEmptyWalkFixture(fixture?: string | null): boolean {
    return (PWA_TRUE_EMPTY_WALK_FIXTURES as readonly string[]).includes(fixture || '');
}

/** Hosting createSot / trueEmptySot / jobsSot win over local previews. Paint fixtures never write. */
export function pwaCreateFixtureKind(
    sot?: string | null,
    preview?: string | null,
    createSot?: string | null,
    createPreview?: string | null,
    trueEmpty?: string | null,
): string | null {
    if (createSot) return createSot;
    if (createPreview) return createPreview;
    if (trueEmpty) return trueEmpty;
    if (sot) return 'sot';
    if (preview) return 'preview';
    return null;
}

export function pwaCreateGuard(
    input: {
        fixture?: string | null;
        offline?: boolean;
        techId?: string | null;
        collectionPrefix?: string | null;
    } = {},
): string | null {
    if ((PWA_CREATE_PAINT_FIXTURES as readonly string[]).includes(input.fixture || '')) {
        return input.fixture || null;
    }
    if (input.offline) return 'offline';
    if (!input.techId || !input.collectionPrefix) return 'denied';
    if (!isTrueEmptyWalkFixture(input.fixture) && pwaLiveWriteRegionError(input.collectionPrefix)) {
        return 'fixture-region';
    }
    return null;
}

export function pwaCreateCopy(surface?: string | null) {
    if (surface === 'sot') return CREATE_SOT;
    if (surface === 'preview') return CREATE_PREVIEW;
    if (surface === 'true-empty' || surface === 'true-empty-preview') return CREATE_TRUE_EMPTY_CONFIRM;
    if (surface === 'offline') return CREATE_OFFLINE;
    if (surface === 'denied') return CREATE_DENIED;
    if (surface === 'fixture-region') return CREATE_FIXTURE_REGION;
    if (surface === 'error') return CREATE_ERROR;
    if (surface === 'pending') return CREATE_PENDING;
    if (surface === 'cancelled') return CREATE_CANCELLED;
    return null;
}

export function pwaCreatePath(fixture?: string | null, liveEmpty?: boolean): string {
    if (fixture === 'sot' || fixture === 'preview') return 'sot';
    if (isTrueEmptyWalkFixture(fixture)) return 'true-empty-walk';
    if (liveEmpty) return 'true-empty-live';
    return 'live';
}

function requiredCreateString(value: unknown, key: string): string {
    const text = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
    if (!text) {
        throw new Error(`${key} is required.`);
    }
    return text;
}

export type PwaCreateJobValues = {
    type?: unknown;
    damage?: unknown;
    date?: unknown;
    shop?: unknown;
    customerName?: unknown;
    contact?: unknown;
    vehicle?: { year?: unknown; make?: unknown; model?: unknown; insurance?: unknown };
    notes?: unknown;
    waiting?: unknown;
    dropped?: unknown;
    invoiced?: unknown;
};

export function buildCreatePwaJobDoc(
    values: PwaCreateJobValues = {},
    input: { techId?: string | null } = {},
): Record<string, unknown> {
    const techId = requiredCreateString(input.techId, 'techId');
    const vehicle = values.vehicle && typeof values.vehicle === 'object' ? values.vehicle : {};
    return {
        type: requiredCreateString(values.type, 'type'),
        damage: requiredCreateString(values.damage, 'damage'),
        date: requiredCreateString(values.date, 'date'),
        shop: requiredCreateString(values.shop, 'shop'),
        customerName: requiredCreateString(values.customerName, 'customerName'),
        contact: requiredCreateString(values.contact, 'contact'),
        vehicle: {
            year: requiredCreateString(vehicle.year, 'year'),
            make: requiredCreateString(vehicle.make, 'make'),
            model: requiredCreateString(vehicle.model, 'model'),
            insurance: requiredCreateString(vehicle.insurance, 'insurance'),
        },
        assignee: techId,
        notes: typeof values.notes === 'string' ? values.notes : '',
        status: 'pending',
        invoiced: values.invoiced === true,
        waiting: values.waiting === true,
        dropped:
            typeof values.dropped === 'string' && values.dropped.trim()
                ? values.dropped.trim()
                : 'Currently dropped',
    };
}

export function parseCreatePwaJobResult(data: { id?: unknown } | null | undefined) {
    const jobId = data && typeof data.id === 'string' ? data.id : '';
    return {
        success: Boolean(jobId),
        jobId,
        status: jobId ? 'pending' : '',
    };
}

export function pwaCreateWriteError(error: unknown): string {
    return rejectionMessage(error, 'Assigned job could not be written.');
}

export function addPwaJob(
    collectionPath: string,
    doc: Record<string, unknown>,
    add: (path: string, payload: Record<string, unknown>) => Promise<{ id?: string }> = (path, payload) =>
        db.collection(path).add(payload),
) {
    return add(collectionPath, doc);
}

type SetPwaJob = (
    path: string,
    id: string,
    payload: Record<string, unknown>,
) => Promise<{ id?: string } | void>;

/** Overwrite one client-chosen id. A timed-out create retries this doc instead of add(). */
export function setPwaJob(
    collectionPath: string,
    jobId: string,
    doc: Record<string, unknown>,
    set: SetPwaJob = async (path, id, payload) => {
        await db.collection(path).doc(id).set(payload);
        return { id };
    },
) {
    return Promise.resolve(set(collectionPath, jobId, doc)).then((result) => ({
        id: result && typeof result.id === 'string' && result.id ? result.id : jobId,
    }));
}

export async function createPwaJobFromForm(
    values: PwaCreateJobValues,
    input: {
        techId?: string | null;
        collectionPrefix?: string | null;
        jobId?: string | null;
        addJob?: (path: string, payload: Record<string, unknown>) => Promise<{ id?: string }>;
        setJob?: SetPwaJob;
    } = {},
) {
    const collectionPrefix = requiredCreateString(input.collectionPrefix, 'collectionPrefix');
    const regionError = pwaLiveWriteRegionError(collectionPrefix);
    if (regionError) throw new Error(regionError);
    const doc = buildCreatePwaJobDoc(values, { techId: input.techId });
    const jobId = typeof input.jobId === 'string' ? input.jobId.trim() : '';
    const data = jobId
        ? await setPwaJob(`${collectionPrefix}/jobs`, jobId, doc, input.setJob)
        : await addPwaJob(`${collectionPrefix}/jobs`, doc, input.addJob);
    const parsed = parseCreatePwaJobResult(data);
    if (!parsed.success || parsed.status !== 'pending' || !parsed.jobId) {
        throw new Error('Firestore add did not return a pending job.');
    }
    return parsed;
}
